import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { showErrorToast } from 'services/toast/redux/actions'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { fromJS, Map } from 'immutable'
import _debounce from 'lodash/debounce'
import { useQuery, useLazyQuery } from '@apollo/client'
import { Alert, Grid, Box } from '@mui/material'
import { MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS } from 'services/media-assets/provider-urls'
import { LANGUAGE_EN } from 'services/languages/constants'
import {
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE,
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
} from 'services/media-assets/reference-types'
import { getMediaInfo } from 'services/media'
import MuiButton from 'gui/components/common/MuiButton'
import VideoPlayer from 'gui/components/video-player'
import Link from 'gui/components/link'
import { TYPE_BRIGHTCOVE_STUDIO } from 'gui/components/link/types'
import MediaAutoComplete from 'gui/components/media-auto-complete'
import { Media, MediaAsset } from '@/__generated__/gql/graphql'
import { GET_MEDIA_BY_ID, GET_MEDIA_SUGGESTIONS } from './queries'
import { getVideoUrl, isMediaPlayable } from '../mediaUtils'
import { getDateWithoutTime } from '../helpers'
import '../index.scss'

type MediaData = {
  mediaList?: {
    media?: Media[] | null
  } | null
}

type MediaInfo = {
  mediaUrls: Record<string, string> | null
  textTracks: {
    thumbnail: string | null
  } | null
}

interface ContentMediaFieldProps {
  name: string
  mediaId: string | null
  onChange: (
    event: {
      target: { name: string; value: string | null }
    },
    record?: { contentId: string; mediaId: string },
  ) => void
  disabled: boolean
  clearMediaField: (name: string) => void
  contentLanguage?: string | null
  primaryLanguage?: string | null
}

/**
 * Renders a single media field with autocomplete, preview, and metadata.
 */
function ContentMediaField ({
  name,
  mediaId,
  onChange,
  disabled,
  clearMediaField,
  contentLanguage = null,
  primaryLanguage = null,
}: ContentMediaFieldProps) {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const [notPlayable, setNotPlayable] = useState(false)
  const [loadingMediaInfo, setLoadingMediaInfo] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [suggestions, setSuggestions] = useState<Partial<Media>[]>([])
  const [mediaInfo, setMediaInfo] = useState<MediaInfo | null>(null)

  const [queryMediaSuggestions, { data: mediaSuggestionsData, loading: loadingMediaSuggestions }] =
    useLazyQuery<MediaData>(GET_MEDIA_SUGGESTIONS)

  // Debounce the query media suggestions
  const queryMediaSuggestionsDebounced = useRef(
    _debounce((value: string) => {
      if (value) {
        queryMediaSuggestions({ variables: { contentId: `%${value}%`, limit: 25 } })
      } else {
        setSuggestions([])
      }
    }, 300),
  )

  // Fetch media by mediaId when mediaId changes
  const {
    data: mediaData,
    error: mediaError,
    loading: loadingMedia,
    refetch: refetchMedia,
  } = useQuery<MediaData>(GET_MEDIA_BY_ID, {
    variables: { mediaId },
    skip: !mediaId,
  })

  // Show toast when media query errors (Apollo 4: use effect instead of onError)
  useEffect(() => {
    if (mediaError) {
      dispatch(showErrorToast(`Loading media failed: ${mediaError.message}`))
    }
  }, [mediaError, dispatch])

  // Fetch media info (providerKey, mediaUrls, textTracks)
  const getMediaInfoData = useCallback(async () => {
    if (!mediaId) {
      setMediaInfo(null)
      setLoadingMediaInfo(false)
      return
    }
    setLoadingMediaInfo(true)
    const mediaInfoData = await getMediaInfo({
      auth,
      server: brooklynUrl,
      mediaId: Number(mediaId),
    })
    setMediaInfo(mediaInfoData.toJS())
    setLoadingMediaInfo(false)
  }, [auth, brooklynUrl, mediaId])

  const media = useMemo(() => {
    const mediaList = mediaData?.mediaList?.media
    return mediaList && mediaList.length > 0 ? mediaList[0] : null
  }, [mediaData])

  // Prefer asset: null lang → content language → primary language → en → rest; then first with providerKey
  const preferredAsset = useMemo(() => {
    const assets = media?.mediaAssets?.filter((a): a is MediaAsset => a != null) ?? []
    const sorted = [...assets].sort((a, b) => {
      const langA = a.language ?? null
      const langB = b.language ?? null
      if (langA === null) return -1
      if (langB === null) return 1
      if (langA === contentLanguage) return -1
      if (langB === contentLanguage) return 1
      if (primaryLanguage && langA === primaryLanguage) return -1
      if (primaryLanguage && langB === primaryLanguage) return 1
      if (langA === LANGUAGE_EN) return -1
      if (langB === LANGUAGE_EN) return 1
      return 0
    })
    return sorted.find((a) => a?.providerKey) ?? sorted[0] ?? null
  }, [media, contentLanguage, primaryLanguage])

  const providerKey = useMemo(() => preferredAsset?.providerKey ?? null, [preferredAsset])

  const mediaAssets = useMemo(() => media?.mediaAssets || [], [media])

  // Subtitles must be passed to VideoPlayer as an immutable list
  const subtitles = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (asset: MediaAsset | null) =>
            asset?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE,
        ),
      ),
    [mediaAssets],
  )

  // Captions must be passed to VideoPlayer as an immutable list
  const captions = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (asset: MediaAsset | null) =>
            asset?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
        ),
      ),
    [mediaAssets],
  )

  const fallbackSubtitles = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (a) =>
            a?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE &&
            a?.language === contentLanguage,
        ),
      ),
    [mediaAssets, contentLanguage],
  )

  const fallbackCaptions = useMemo(
    () =>
      fromJS(
        mediaAssets.filter(
          (a) =>
            a?.mediaReferenceType === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION &&
            a?.language === contentLanguage,
        ),
      ),
    [mediaAssets, contentLanguage],
  )

  // Media URLs must be passed to VideoPlayer as an immutable map
  const mediaUrls = fromJS(mediaInfo?.mediaUrls ?? Map())
  const textTracksThumbnail = mediaInfo?.textTracks?.thumbnail ?? null

  // When content has a language, we require language-specific media here (no fallback to primary).
  // Shared getVideoUrl keeps its fallback; we only treat "missing" as error in this component.
  const hasLangSpecificUrl = useMemo(() => {
    if (!contentLanguage) return true
    const byLang = mediaUrls.get?.('byLang')
    return !!(
      byLang &&
      (byLang.get?.(contentLanguage) ?? (byLang as Record<string, string>)[contentLanguage])
    )
  }, [contentLanguage, mediaUrls])
  const hasLangSpecificAsset = useMemo(
    () => !contentLanguage || !!media?.mediaAssets?.some((a) => a?.language === contentLanguage),
    [contentLanguage, media?.mediaAssets],
  )
  const missingLanguageSpecificMedia =
    !!contentLanguage && (!hasLangSpecificUrl || !hasLangSpecificAsset)

  const videoUrl = getVideoUrl(mediaUrls, { language: contentLanguage ?? undefined })

  const fallbackVideoUrl = useMemo(() => {
    if (!missingLanguageSpecificMedia) return null
    const enUrl = getVideoUrl(mediaUrls, { language: LANGUAGE_EN })
    if (enUrl) return enUrl
    return getVideoUrl(mediaUrls, {})
  }, [missingLanguageSpecificMedia, mediaUrls])

  const hasFallbackVideo = !!fallbackVideoUrl

  const effectiveVideoUrl = missingLanguageSpecificMedia ? (fallbackVideoUrl ?? '') : videoUrl

  const metaData = useMemo(
    () => [
      {
        label: 'Media ID',
        value: (
          <Link absolute={true} href={`/media/${mediaId}`}>
            {mediaId}
          </Link>
        ),
      },
      {
        label: 'BC Video ID',
        value: providerKey && (
          <Link
            className="media-detail__provider-link"
            type={TYPE_BRIGHTCOVE_STUDIO}
            href={`${MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS}/${providerKey}`}
          >
            {providerKey}
          </Link>
        ),
      },
      {
        label: 'Media Maestro Code',
        value: media?.mediaMaestroTitleCode,
      },
      {
        label: 'Policy ID',
        value: media?.policyId,
      },
      {
        label: 'Video Updated at',
        value: getDateWithoutTime(media?.updatedAt),
      },
      {
        label: 'Duration',
        value: media?.duration,
      },
    ],
    [media, mediaId, providerKey],
  )

  const handleSuggestionSelect = (
    _event: React.ChangeEvent<HTMLInputElement>,
    record: { contentId: string; mediaId: string },
  ) => {
    if (record && record.mediaId) {
      onChange({ target: { name, value: record.mediaId } }, record)
      setSearchValue(record.contentId)
      setSuggestions([])
      // Refetch media data
      if (record.mediaId) {
        refetchMedia({ mediaId: record.mediaId })
      }
    } else {
      onChange({ target: { name, value: null } })
      setSearchValue('')
      setSuggestions([])
    }
  }

  const handleRemoveMedia = () => {
    onChange({ target: { name, value: null } })
    clearMediaField(name)
    setSearchValue('')
    setNotPlayable(false)
  }

  const handleAutocompleteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchValue(value)
    queryMediaSuggestionsDebounced.current(value.trim())
  }

  // Update suggestions when search value changes
  useEffect(() => {
    setSuggestions(mediaSuggestionsData?.mediaList?.media ?? [])
  }, [mediaSuggestionsData])

  // Fetch media info when mediaId changes
  useEffect(() => {
    if (mediaId) {
      getMediaInfoData()
    }
  }, [mediaId, getMediaInfoData])

  // Update search value when contentId changes
  useEffect(() => {
    if (media?.contentId) {
      setSearchValue(media.contentId)
      setSuggestions([])
    }
  }, [media?.contentId])

  // Check if media is playable
  useEffect(() => {
    if (!mediaId || !media) {
      setNotPlayable(false)
      return
    }
    setNotPlayable(!isMediaPlayable(fromJS(media), effectiveVideoUrl))
  }, [mediaId, media, effectiveVideoUrl])

  const isLoading = loadingMedia || loadingMediaInfo || loadingMediaSuggestions

  return (
    <div key={`content-media-${name}`}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <MediaAutoComplete
            value={searchValue}
            name={name}
            onChange={handleAutocompleteChange}
            onSuggestionSelect={handleSuggestionSelect}
            disabled={disabled}
            placeholder={'Search or select media...'}
            suggestions={suggestions}
          />
        </Grid>
      </Grid>
      {mediaId && notPlayable && !isLoading && (
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Alert severity="warning">
              Warning: This media is not playable. Please ensure the media has valid video URLs
              before publishing.
            </Alert>
          </Grid>
        </Grid>
      )}
      {mediaId && !isLoading && media && missingLanguageSpecificMedia && hasFallbackVideo && (
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Alert severity="info">
              Video is not available in {contentLanguage}. Playing in English with {contentLanguage}{' '}
              subtitles when available.
            </Alert>
          </Grid>
        </Grid>
      )}
      {mediaId && !isLoading && media && missingLanguageSpecificMedia && !hasFallbackVideo && (
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Alert severity="error">
              No media asset is available for this content&apos;s language ({contentLanguage}). Add
              a {contentLanguage} media asset to this media (
              <Link absolute={true} href={`/media/${mediaId}`}>
                Media {mediaId}
              </Link>
              ) to display the video here.
            </Alert>
          </Grid>
        </Grid>
      )}
      {mediaId && !isLoading && media && (!missingLanguageSpecificMedia || hasFallbackVideo) && (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={7} lg={8}>
            <VideoPlayer
              src={
                missingLanguageSpecificMedia
                  ? (fallbackVideoUrl as string)
                  : getVideoUrl(mediaUrls, { language: contentLanguage ?? undefined })
              }
              srcTextTrackThumnail={textTracksThumbnail}
              subtitles={missingLanguageSpecificMedia ? fallbackSubtitles : subtitles}
              captions={missingLanguageSpecificMedia ? fallbackCaptions : captions}
            />
          </Grid>
          <Grid item xs={12} md={5} lg={4}>
            <Box className="modern-info-box">
              {metaData.map(({ label, value }) => (
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={label}>
                  <Grid item xs={5} sx={{ fontWeight: 500, color: '#555' }}>
                    {label}
                  </Grid>
                  <Grid item xs={7} sx={{ pl: 2 }}>
                    {value || <span style={{ color: '#aaa' }}>—</span>}
                  </Grid>
                </Grid>
              ))}
              {!disabled && (
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <MuiButton color="error" style={{ marginTop: 8 }} onClick={handleRemoveMedia}>
                    Remove Media
                  </MuiButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default ContentMediaField
